import { useWindowDimensions } from "@luce/core";
import type { IconName } from "@luce/ui";
import { ActionButton, AdaptiveModal, Box, Button } from "@luce/ui";

import type { LinkShareChannel, ShareModalProps } from "./types";
import { useShareMessage } from "./use-share-message";

const CHANNELS: { channel: LinkShareChannel; icon: IconName; label: string }[] = [
  { channel: "whatsapp", icon: "WhatsappLogo", label: "WhatsApp" },
  { channel: "telegram", icon: "TelegramLogo", label: "Telegram" },
  { channel: "messenger", icon: "MessengerLogo", label: "Messenger" },
  { channel: "sms", icon: "ChatDots", label: "SMS" },
];

/**
 * Share sheet for any message (gift codes, referral invites, …): one-tap
 * channels plus the native share sheet and copy. Dialog on desktop, bottom
 * sheet on mobile.
 */
export function ShareModal({
  open,
  onOpenChange,
  message,
  title = "Share this code with family and friends",
  onShare,
}: ShareModalProps) {
  const { isDesktop } = useWindowDimensions();
  const { shareTo, shareOther, copy } = useShareMessage(message, onShare);

  return (
    <AdaptiveModal.Root open={open} onOpenChange={onOpenChange} dialogSize="md">
      <AdaptiveModal.Header>
        <Box className="gap-spacing-md flex flex-row items-center">
          <AdaptiveModal.Title className="flex-1">{title}</AdaptiveModal.Title>
          {isDesktop && <AdaptiveModal.Close />}
        </Box>
      </AdaptiveModal.Header>

      <AdaptiveModal.Body>
        <Box className="flex flex-row justify-between">
          {CHANNELS.map(({ channel, icon, label }) => (
            <ActionButton
              key={channel}
              icon={icon}
              label={label}
              variant="secondary"
              onClick={() => shareTo(channel)}
              className="flex-1"
              size="md"
            />
          ))}
        </Box>
      </AdaptiveModal.Body>

      <AdaptiveModal.Footer>
        <Box className="gap-spacing-sm flex w-full flex-1 flex-row">
          <Box className="flex-1">
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              leadingIcon="DotsThreeOutline"
              label="Other"
              onClick={shareOther}
            />
          </Box>
          <Box className="flex-1">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              leadingIcon="Copy"
              label="Copy"
              onClick={copy}
            />
          </Box>
        </Box>
      </AdaptiveModal.Footer>
    </AdaptiveModal.Root>
  );
}
